(ns common.transaction-process.templates.capman.reservation-prefill
  "CapMan-specific, deliberately one-off logic.

   When a PM decision is created, CapMan wants its investment-reservation rows
   pre-filled from the preparation-stage amount fields. They created
   `:entity.type/investment-reservation-type` entities titled to match the
   required reservation categories. Because those types are runtime-defined per
   tenant, we cannot reference them by id — so we match the configured category
   label against the reservation-type *title*
   (case-insensitive). This awkward label-based matching is intentionally
  isolated in this namespace so it's obvious it is custom CapMan behaviour and
  not a general transaction-process feature.

   Wired via a slot's `:hook/prefill :capman.tp/reservation-prefill` (see
   `common.transaction-process.templates.capman.hooks`), so the shared engine
  invokes it by keyword without importing this namespace."
  (:require
   #?(:cljs [common.number-utils :as nu])
   [common.util :as cu]))

(def reservation-mappings
  "Ordered PM reservation categories and their preparation-stage amount sources.
   Multiple source fields are summed only when their effective currencies agree."
  [{:category-label "Reserved equity for investment"
    :source-fields  [:transaction-process/equity-amount
                     :transaction-process/transaction-costs
                     :transaction-process/capital-transfer-tax]}
   {:category-label "Currency buffer"
    :source-fields  [:transaction-process/currency-buffer]}
   {:category-label "Equity guarantee"
    :source-fields  [:transaction-process/guarantee-amount]}
   {:category-label "Earn-out"
    :source-fields  [:transaction-process/earnout-amount]}])

(defn- types-by-title
  [reservation-types]
  (->> reservation-types
       (remove :common/disabled?)
       (remove :common/archived?)
       (reduce (fn [acc t]
                 (if-let [title (:investment-reservation-type/title t)]
                   (assoc acc (cu/str-trim-lcase title) t)
                   acc))
               {})))

(defn- numeric-value
  [value]
  #?(:clj value
     :cljs (nu/->float value)))

(defn- reservation-values
  [process-entity source-fields default-currency]
  (let [populated-fields (filter (fn [field-kw]
                                   (some? (get process-entity field-kw)))
                                 source-fields)
        amounts          (map (fn [field-kw]
                                (numeric-value (get process-entity field-kw)))
                              populated-fields)
        currencies       (->> populated-fields
                              (map (fn [field-kw]
                                     (or (get process-entity (cu/currency->symbol field-kw))
                                         default-currency)))
                              distinct
                              vec)]
    (when (and (seq amounts)
               (every? number? amounts)
               (= 1 (count currencies)))
      {:amount   (reduce + amounts)
       :currency (first currencies)})))

(defn build-prefill-reservations
  "Build `[temp-id reservation-entity]` pairs to seed a decision's
   `:decision/investment-reservations`. One row per configured mapping that has
   numeric source values with one shared currency and whose category label
   matches an available reservation-type title.

   Each row gets a `:common/ord` stamped from its position in the mapping order,
   so the prefilled rows render in that importance order and stay stably sorted
   across reloads (ref-many ordering is otherwise unstable). Manually added rows
   later get `max+1`, appending to the end.

   Args:
   - `:reservation-mappings` ordered category/source-field specifications
   - `:process-entity`    the resolved transaction-process entity (prep values)
   - `:reservation-types` available `:entity.type/investment-reservation-type`s
   - `:create-temp-id`    0-arg fn returning a fresh temp id
   - `:default-currency`  fallback currency symbol when a field has no `--sym`"
  [{:keys [reservation-mappings process-entity reservation-types create-temp-id default-currency]}]
  (let [by-title (types-by-title reservation-types)]
    (->> reservation-mappings
         (keep (fn [{:keys [category-label source-fields]}]
                 (let [{:keys [amount currency]} (reservation-values process-entity
                                                                     source-fields
                                                                     default-currency)
                       res-type (get by-title (cu/str-trim-lcase category-label))]
                   (when (and (some? amount) res-type)
                     (let [temp-id (create-temp-id)
                           sym     currency]
                       [temp-id {:db/id                               temp-id
                                 :entity/type                         :entity.type/investment-reservation
                                 :investment-reservation/type         (:db/id res-type)
                                 :investment-reservation/amount       amount
                                 :investment-reservation/amount--sym  sym}])))))
         (map-indexed (fn [idx [temp-id entity]]
                        [temp-id (assoc entity :common/ord idx)]))
         vec)))
